/**
 * sandbox iframe に流し込むHTMLの組み立て。
 * client.js と CSS は untrusted なので、HTML本文へ直接書かず JSON payload に載せ、
 * bootstrap が textContent で注入する（`</script>` を含むソースでHTMLを壊されないため）。
 */

import picoCss from '@picocss/pico/css/pico.min.css?raw';
import frameThemeCss from './frameTheme.css?raw';

/**
 * 基盤が埋め込むscript/styleとdata画像だけを許可する。
 * connect-src 'none' により、client.js は fetch/XHR/WebSocket を使えない。
 * 外部通信が要るUIは RPC を通じてserver側の ctx.http.request を使う。
 */
export const FRAME_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  'img-src data:',
  "connect-src 'none'",
  "frame-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
  "form-action 'none'",
].join('; ');

/**
 * allow-scripts のみ。allow-same-origin を付けるとopaque originが失われ、
 * DOM/cookie/storageの分離とorigin検証（event.origin === 'null'）が成立しなくなる。
 * allow-popups / allow-modals も付けないため、window.open と alert はブラウザ側で止まる。
 */
export const FRAME_SANDBOX = 'allow-scripts';

export interface FrameContent {
  workspaceId: string;
  appId: string;
  /** microappのclient.js。未信頼。 */
  javascript: string;
  /** microappのCSS。未信頼。 */
  css: string;
  /** 表示用のタイトル。 */
  title: string;
  /** sdk/typescript/src/bootstrap.ts をIIFEへbundleしたソース。信頼側。 */
  bootstrapScript: string;
  /** このsrcdocだけに有効な使い捨ての値。readyの出所を確かめるために使う。 */
  nonce: string;
}

export function buildFrameHtml(content: FrameContent): string {
  const payload = {
    workspaceId: content.workspaceId,
    appId: content.appId,
    clientJavaScript: content.javascript,
    clientCss: content.css,
    nonce: content.nonce,
  };

  return (
    // data-theme="light" は Pico のライトテーマを固定し、frameTheme.css の上書きを効かせる印。
    '<!doctype html><html lang="ja" data-theme="light"><head><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(FRAME_CSP)}">` +
    `<title>${escapeText(content.title)}</title>` +
    `<style>${FRAME_BASE_CSS}</style>` +
    '</head><body>' +
    `<script>window.__MICROAPP_BOOTSTRAP__=${embedJson(payload)}</script>` +
    `<script>${content.bootstrapScript}</script>` +
    '</body></html>'
  );
}

/**
 * frame の既定スタイル。Pico CSS（素の HTML 要素に見た目を付ける CSS フレームワーク）に、
 * Shell の見た目へ寄せる上書き（frameTheme.css）を重ねたもの。microapp が何も書かなくても
 * 入力欄・ボタン・表・見出しが整い、microapp 側の CSS（ui.style）は後から読み込まれるので
 * 上書きできる。
 *
 * これは信頼側のCSSなので <style> へ直接書く。未信頼の client CSS とは違い bootstrap 経由に
 * しない。その代わり `</style>` を含まないことをここで確かめる（含んでいれば HTML が壊れる）。
 */
export const FRAME_BASE_CSS = checkedStyle(`${picoCss}\n${frameThemeCss}`);

function checkedStyle(css: string): string {
  if (/<\/style/i.test(css)) throw new Error('frame base CSS must not contain </style>');
  return css;
}

/**
 * `<script>` の中へJSONを置くための埋め込み。
 * `<` をユニコードエスケープにすることで `</script>` による早期終了を防ぐ。
 * U+2028 / U+2029 はJSONでは合法だがJavaScriptの行終端子なので同じくエスケープする。
 */
function embedJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * srcdocごとの使い捨ての値。
 * 同じiframe要素へsrcdocを入れ直すと、置き換えられる前の文書のreadyが後から届く。
 * WindowProxyはnavigationを跨いで同一なのでsourceでは区別できず、
 * これが無いと古い文書へportを渡してしまい、新しい文書は永久に接続できない。
 */
export function createFrameNonce(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `nonce_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttribute(text: string): string {
  return escapeText(text).replace(/"/g, '&quot;');
}
